"use client";

import Script from "next/script";
import { useEffect, useState } from "react";

import {
  CONSENT_EVENT,
  type CookieConsentValue,
  readConsent,
} from "@/lib/public/consent";

type WindowWithGtag = Window & {
  gtag?: (
    command: string,
    action: string,
    params?: Record<string, string | number | boolean>,
  ) => void;
};

/**
 * Analytics (GA4/GTM) and marketing (Meta Pixel) tags consent handling.
 *
 * GA4 and GTM use Google Consent Mode v2: tags are rendered in HTML
 * with default 'denied' and updated to 'granted' as soon as the visitor accepts.
 * Meta Pixel mounts only after marketing consent has been explicitly granted.
 */
export function ConsentScripts({
  fb,
}: {
  ga?: string | null;
  gtm?: string | null;
  fb: string | null;
}) {
  const [consent, setConsent] = useState<CookieConsentValue | null>(null);

  useEffect(() => {
    const sync = () => {
      const current = readConsent();
      setConsent(current);

      const win = window as WindowWithGtag;
      if (typeof win.gtag === "function") {
        if (current?.analytics) {
          win.gtag("consent", "update", {
            analytics_storage: "granted",
            ad_storage: current.marketing ? "granted" : "denied",
            ad_user_data: current.marketing ? "granted" : "denied",
            ad_personalization: current.marketing ? "granted" : "denied",
          });
        } else if (current && !current.analytics) {
          win.gtag("consent", "update", {
            analytics_storage: "denied",
            ad_storage: "denied",
            ad_user_data: "denied",
            ad_personalization: "denied",
          });
        }
      }
    };

    sync();
    window.addEventListener(CONSENT_EVENT, sync);
    return () => window.removeEventListener(CONSENT_EVENT, sync);
  }, []);

  const allowMarketing = consent?.marketing;

  return (
    <>
      {fb && allowMarketing ? (
        <>
          <noscript>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              height="1"
              width="1"
              style={{ display: "none" }}
              alt=""
              src={`https://www.facebook.com/tr?id=${fb}&ev=PageView&noscript=1`}
            />
          </noscript>
          <Script id="fb-pixel-init" strategy="afterInteractive">
            {`!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${fb}');fbq('track','PageView');`}
          </Script>
        </>
      ) : null}
    </>
  );
}
